import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Heart, Skull, Crown, Mic, MicOff, Activity, Video, VideoOff, Upload, Shield, Zap, Gem, FileText, Loader2, Camera, SwitchCamera } from 'lucide-react';
import { DeckPlaytester } from './DeckPlaytester';

// Card type for playtester
interface PlaytesterCard {
    name: string;
    quantity: number;
    imageUrl?: string;
    backImageUrl?: string;
    cmc: number;
    isLand: boolean;
    colors: string[];
    type?: string;
}

// Player means
type PlayerMeans = 'lifeTracker' | 'playtester' | 'webcam';

// Player feature toggles (secondary to means)
interface PlayerFeatures {
    hasMic: boolean;       // voice chat (online only)
    handRevealed: boolean; // whether hand is visible to opponents (playtester only)
}

// Player state
interface Player {
    id: string;
    name: string;
    seatIndex: number;
    life: number;
    poison: number;
    commanderTax: number;
    commanderDamage: number[];
    status: 'active' | 'eliminated';
    isReady: boolean;
    hasRolled: boolean;
    rollValue: number | null;
    means: PlayerMeans;
    features: PlayerFeatures;
    lastActiveAt: number;
    commanderName: string;
    commanderArt: string;
    // Playtester deck data
    deck?: PlaytesterCard[];
    deckName?: string;
    // Extra counters
    experience: number;
    energy: number;
    treasure: number;
}

// Means metadata for inline rendering
const MEANS_META: Record<PlayerMeans, { label: string; icon: string }> = {
    lifeTracker: { label: 'Life Tracker', icon: '❤️' },
    playtester: { label: 'Playtester', icon: '🃏' },
    webcam: { label: 'Webcam', icon: '📹' },
};

interface PlayerContainerProps {
    player: Player;
    isCurrentTurn?: boolean;
    isFocused?: boolean;
    onLifeChange: (delta: number) => void;
    onPoisonChange: (delta: number) => void;
    onCommanderTaxChange: (delta: number) => void;
    onCommanderDamageChange?: (fromPlayerIndex: number, delta: number) => void;
    onFeaturesChange?: (features: Partial<PlayerFeatures>) => void;
    onMeansChange?: (means: PlayerMeans) => void;
    onLoadDeck?: () => void;
    onClearDeck?: () => void;
    onDeckImported?: (deck: PlaytesterCard[], deckName?: string) => void;
    onEliminate?: () => void;
    showCommanderDamage?: boolean;
    otherPlayers?: Player[];
    compact?: boolean;
    streamMode?: boolean;
    isOnline?: boolean;
    // Extra counter callbacks
    onExperienceChange?: (delta: number) => void;
    onEnergyChange?: (delta: number) => void;
    onTreasureChange?: (delta: number) => void;
    // World state indicators
    isMonarch?: boolean;
    hasInitiative?: boolean;
    hasCityBlessing?: boolean;
    // Turn timer
    turnTimeRemaining?: number;
    // Lobby setup props — when true, header shows editable controls
    isLobby?: boolean;
    onNameChange?: (name: string) => void;
    onCommanderNameChange?: (name: string) => void;
    availableMeans?: PlayerMeans[];
    showCommanderField?: boolean;
    // Webcam props
    mediaStream?: MediaStream | null;
    onToggleVideo?: () => void;
    onToggleAudio?: () => void;
    onRequestCamera?: () => void;
    onSwitchCamera?: () => void;
    videoEnabled?: boolean;
    audioEnabled?: boolean;
    isLocalPlayer?: boolean;
}

/**
 * PlayerContainer - SpellTable-style player tile
 * 
 * Structure:
 * ┌─────────────────────────────────┐
 * │ ① Name │ Commander │ badges    │  ← Thin top bar
 * ├─────────────────────────────────┤
 * │                                 │
 * │          ❤️  40                 │  ← Hero life (fills card)
 * │    -1              +1           │  ← Tap halves
 * │  -5                      +5    │  ← Corner buttons on hover
 * │                                 │
 * ├─────────────────────────────────┤
 * │ ☠0 👑+0 ①0 ②0 ③0             │  ← Compact bottom bar
 * └─────────────────────────────────┘
 */
export function PlayerContainer({
    player,
    isCurrentTurn = false,
    isFocused = false,
    onLifeChange,
    onPoisonChange,
    onCommanderTaxChange,
    onCommanderDamageChange,
    onFeaturesChange,
    onMeansChange,
    onLoadDeck,
    onClearDeck,
    onDeckImported,
    onEliminate,
    showCommanderDamage = true,
    otherPlayers = [],
    compact = false,
    streamMode = false,
    isOnline = false,
    onExperienceChange,
    onEnergyChange,
    onTreasureChange,
    isMonarch = false,
    hasInitiative = false,
    hasCityBlessing = false,
    turnTimeRemaining,
    isLobby = false,
    onNameChange,
    onCommanderNameChange,
    availableMeans = [],
    showCommanderField = false,
    mediaStream,
    onToggleVideo,
    onToggleAudio,
    onRequestCamera,
    onSwitchCamera,
    videoEnabled = true,
    audioEnabled = true,
    isLocalPlayer = false,
}: PlayerContainerProps) {

    const contentType = player.means;

    const getLifeColor = (life: number): string => {
        if (life <= 0) return 'text-red-500';
        if (life <= 5) return 'text-red-400';
        if (life <= 10) return 'text-orange-400';
        if (life <= 20) return 'text-amber-400';
        return 'text-white';
    };

    return (
        <div
            className={`
                group/card relative flex flex-col rounded-xl overflow-hidden transition-all h-full
                ${player.status === 'eliminated'
                    ? 'bg-red-900/40 border border-red-500/20 opacity-60'
                    : 'bg-slate-900/80 border border-white/5'
                }
                ${isCurrentTurn ? 'ring-1 ring-amber-400/60 shadow-lg shadow-amber-400/10' : ''}
                ${isFocused ? 'ring-1 ring-cyan-400/60' : ''}
            `}
        >
            {/* ============ COMMANDER ART BACKGROUND ============ */}
            {player.commanderArt && (
                <div
                    className="absolute inset-0 z-0"
                    style={{
                        backgroundImage: `url(${player.commanderArt})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center 20%',
                        opacity: 0.15,
                        filter: 'blur(1px)',
                    }}
                />
            )}

            {/* ============ TOP BAR ============ */}
            {isLobby ? (
                /* ── Lobby mode: solid bar with editable inputs ── */
                <div className="flex-shrink-0 flex items-center justify-between px-2 py-1 bg-black/40 backdrop-blur-sm border-b border-white/10 relative z-10 gap-1">
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                        <span className="w-4 h-4 rounded-full bg-purple-500/30 text-purple-300 text-[9px] flex items-center justify-center font-bold flex-shrink-0">
                            {player.seatIndex + 1}
                        </span>
                        <input
                            type="text"
                            value={player.name}
                            onChange={(e) => onNameChange?.(e.target.value)}
                            className="text-white font-medium text-xs bg-transparent border-b border-white/10 focus:border-purple-400 outline-none truncate px-0.5 w-[70px] min-w-0"
                            placeholder="Name"
                        />
                        {showCommanderField && (
                            <input
                                type="text"
                                value={player.commanderName}
                                onChange={(e) => onCommanderNameChange?.(e.target.value)}
                                className="text-white/50 text-[10px] w-[70px] bg-transparent border-b border-white/5 focus:border-amber-400/50 outline-none truncate px-0.5 italic min-w-0 hidden sm:block"
                                placeholder="Cmdr..."
                            />
                        )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        {availableMeans.length > 0 && (
                            <div className="flex gap-0.5">
                                {availableMeans.map(m => (
                                    <button
                                        key={m}
                                        onClick={() => onMeansChange?.(m)}
                                        className={`px-1 py-0.5 rounded text-[8px] font-medium transition-all ${player.means === m
                                            ? m === 'playtester'
                                                ? 'bg-purple-500/30 border border-purple-500/50 text-purple-300'
                                                : m === 'webcam'
                                                    ? 'bg-cyan-500/30 border border-cyan-500/50 text-cyan-300'
                                                    : 'bg-amber-500/30 border border-amber-500/50 text-amber-300'
                                            : 'bg-white/5 border border-white/10 text-white/30 hover:bg-white/10'
                                            }`}
                                        title={MEANS_META[m].label}
                                    >
                                        {MEANS_META[m].icon}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                /* ── Active mode: floating overlay — hidden by default, shows on hover ── */
                <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-2 py-1 bg-gradient-to-b from-black/60 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity duration-200 pointer-events-none">
                    <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-white/90 font-medium text-[11px] truncate">
                            {player.name}
                        </span>
                        {player.commanderName && (
                            <span className="text-white/40 text-[9px] truncate max-w-[60px] hidden sm:inline">
                                {player.commanderName}
                            </span>
                        )}
                        {isMonarch && <span className="text-[8px] flex-shrink-0" title="Monarch">👑</span>}
                        {hasInitiative && <Shield className="size-2.5 text-cyan-400 flex-shrink-0" />}
                        {hasCityBlessing && <span className="text-[8px] flex-shrink-0" title="City's Blessing">✨</span>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        {player.status === 'eliminated' && <Skull className="size-3 text-red-400" />}
                        {isOnline && (
                            <button
                                onClick={() => onFeaturesChange?.({ hasMic: !player.features.hasMic })}
                                className={`p-0.5 rounded transition-all pointer-events-auto ${player.features.hasMic ? 'text-cyan-400' : 'text-white/20'}`}
                                title="Toggle Microphone"
                            >
                                <Mic className="size-2.5" />
                            </button>
                        )}
                        {isCurrentTurn && turnTimeRemaining !== undefined && (
                            <span className={`px-1 py-0.5 rounded-full font-mono text-[9px] font-bold ${turnTimeRemaining <= 0 ? 'bg-red-500/30 text-red-400 animate-pulse'
                                : turnTimeRemaining <= 10 ? 'bg-red-500/30 text-red-400 animate-pulse'
                                    : turnTimeRemaining <= 30 ? 'bg-amber-500/20 text-amber-400'
                                        : 'bg-emerald-500/20 text-emerald-400'
                                }`}>
                                {turnTimeRemaining <= 0 ? '⏰' : `${Math.floor(turnTimeRemaining / 60)}:${(turnTimeRemaining % 60).toString().padStart(2, '0')}`}
                            </span>
                        )}
                    </div>
                </div>
            )
            }

            {/* ============ MAIN CONTENT: Hero Life / Playtester / Camera ============ */}
            <div className="flex-1 relative z-10 min-h-0 overflow-hidden">
                {contentType === 'webcam' && (
                    <CameraContent
                        player={player}
                        streamMode={streamMode}
                        mediaStream={mediaStream}
                        onToggleVideo={onToggleVideo}
                        onToggleAudio={onToggleAudio}
                        onRequestCamera={onRequestCamera}
                        onSwitchCamera={onSwitchCamera}
                        videoEnabled={videoEnabled}
                        audioEnabled={audioEnabled}
                        isLocalPlayer={isLocalPlayer}
                        onLifeChange={onLifeChange}
                        compact={compact}
                        isLobby={isLobby}
                    />
                )}
                {contentType === 'playtester' && (
                    <PlaytesterContent
                        player={player}
                        streamMode={streamMode}
                        onLoadDeck={onLoadDeck}
                        onClearDeck={onClearDeck}
                        onDeckImported={onDeckImported}
                    />
                )}
                {contentType === 'lifeTracker' && (
                    <div className="w-full h-full relative select-none">
                        {/* Tap left half = -1 */}
                        <button
                            onClick={() => onLifeChange(-1)}
                            className="absolute inset-y-0 left-0 w-1/2 z-10 cursor-pointer hover:bg-red-500/8 active:bg-red-500/20 transition-colors group/left"
                        >
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-red-400/0 group-hover/left:text-red-400/40 text-2xl font-bold transition-colors select-none">−1</span>
                        </button>
                        {/* Tap right half = +1 */}
                        <button
                            onClick={() => onLifeChange(1)}
                            className="absolute inset-y-0 right-0 w-1/2 z-10 cursor-pointer hover:bg-green-500/8 active:bg-green-500/20 transition-colors group/right"
                        >
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-green-400/0 group-hover/right:text-green-400/40 text-2xl font-bold transition-colors select-none">+1</span>
                        </button>
                        {/* -5 / +5 in corners, visible on hover */}
                        <button onClick={() => onLifeChange(-5)} className="absolute left-1.5 top-1.5 z-20 px-2 py-1 rounded-lg bg-red-500/15 hover:bg-red-500/40 text-red-400 text-[10px] font-bold opacity-0 group-hover/card:opacity-50 hover:!opacity-100 transition-all">-5</button>
                        <button onClick={() => onLifeChange(5)} className="absolute right-1.5 top-1.5 z-20 px-2 py-1 rounded-lg bg-green-500/15 hover:bg-green-500/40 text-green-400 text-[10px] font-bold opacity-0 group-hover/card:opacity-50 hover:!opacity-100 transition-all">+5</button>
                        {/* Hero life number - centered */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <span className={`${compact ? 'text-6xl' : 'text-8xl'} font-bold font-mono leading-none drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)] ${getLifeColor(player.life)}`}>
                                {player.life}
                            </span>
                        </div>
                        {/* Persistent name label — always visible, bottom-left, editable on click */}
                        {!isLobby && (
                            <div className="absolute bottom-1 left-2 z-30">
                                <input
                                    type="text"
                                    value={player.name}
                                    onChange={(e) => onNameChange?.(e.target.value)}
                                    className="text-[10px] text-white/30 hover:text-white/60 focus:text-white font-medium bg-transparent border-none outline-none cursor-text hover:bg-white/5 focus:bg-black/40 focus:border focus:border-white/20 rounded px-1 py-0.5 -ml-1 transition-all w-24"
                                    title="Click to edit name"
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ============ BOTTOM BAR: Compact counters — hover-reveal for clean look ============ */}
            <div className={`flex-shrink-0 flex items-center justify-center gap-1 px-1.5 py-0.5 bg-black/30 border-t border-white/5 relative z-10 text-[9px] flex-wrap transition-opacity duration-200 ${
                /* Always show if any counter is non-zero, otherwise show on hover */
                (player.poison > 0 || player.commanderTax > 0 || player.experience > 0 || player.energy > 0 || player.treasure > 0 || (player.commanderDamage && player.commanderDamage.some(d => d > 0)))
                    ? 'opacity-100'
                    : 'opacity-0 group-hover/card:opacity-100'
                }`}>
                {/* Poison */}
                <div className={`flex items-center gap-px px-1 rounded transition-all cursor-default ${player.poison > 0 ? 'bg-green-500/20 text-green-400' : 'text-white/25 hover:text-white/50'}`}>
                    <span>☠</span>
                    <button onClick={() => onPoisonChange(-1)} className="hover:text-green-300 px-px">-</button>
                    <span className="font-mono w-3 text-center">{player.poison}</span>
                    <button onClick={() => onPoisonChange(1)} className="hover:text-green-300 px-px">+</button>
                </div>
                {/* Commander Tax */}
                {showCommanderDamage && (
                    <div className={`flex items-center gap-px px-1 rounded transition-all cursor-default ${player.commanderTax > 0 ? 'bg-purple-500/20 text-purple-300' : 'text-white/25 hover:text-white/50'}`}>
                        <Crown className="size-2" />
                        <button onClick={() => onCommanderTaxChange(-1)} className="hover:text-purple-200 px-px">-</button>
                        <span className="font-mono w-4 text-center">+{player.commanderTax * 2}</span>
                        <button onClick={() => onCommanderTaxChange(1)} className="hover:text-purple-200 px-px">+</button>
                    </div>
                )}
                {/* Experience */}
                {player.experience > 0 && (
                    <div className="flex items-center gap-px px-1 rounded bg-purple-500/15 text-purple-300">
                        <span>⚗</span>
                        <button onClick={() => onExperienceChange?.(-1)} className="px-px">-</button>
                        <span className="font-mono">{player.experience}</span>
                        <button onClick={() => onExperienceChange?.(1)} className="px-px">+</button>
                    </div>
                )}
                {/* Energy */}
                {player.energy > 0 && (
                    <div className="flex items-center gap-px px-1 rounded bg-yellow-500/15 text-yellow-300">
                        <Zap className="size-2" />
                        <button onClick={() => onEnergyChange?.(-1)} className="px-px">-</button>
                        <span className="font-mono">{player.energy}</span>
                        <button onClick={() => onEnergyChange?.(1)} className="px-px">+</button>
                    </div>
                )}
                {/* Treasure */}
                {player.treasure > 0 && (
                    <div className="flex items-center gap-px px-1 rounded bg-amber-500/15 text-amber-300">
                        <Gem className="size-2" />
                        <button onClick={() => onTreasureChange?.(-1)} className="px-px">-</button>
                        <span className="font-mono">{player.treasure}</span>
                        <button onClick={() => onTreasureChange?.(1)} className="px-px">+</button>
                    </div>
                )}
                {/* Commander Damage — only pills with damage > 0 shown always, rest on hover */}
                {showCommanderDamage && otherPlayers.length > 0 && !compact && otherPlayers.map((op) => {
                    const dmg = player.commanderDamage[op.seatIndex] || 0;
                    return (
                        <div key={op.id} className={`flex items-center gap-px px-0.5 rounded transition-all cursor-default ${dmg > 0 ? (dmg >= 15 ? 'bg-red-500/20 text-red-400' : 'bg-orange-500/15 text-orange-300') : 'text-white/20 hover:text-white/40'}`}>
                            <span className="w-3 h-3 rounded-full bg-purple-500/20 text-purple-300 text-[7px] flex items-center justify-center flex-shrink-0">{op.seatIndex + 1}</span>
                            <button onClick={() => onCommanderDamageChange?.(op.seatIndex, -1)} className="px-px">-</button>
                            <span className="font-mono w-2 text-center">{dmg}</span>
                            <button onClick={() => onCommanderDamageChange?.(op.seatIndex, 1)} className="px-px">+</button>
                        </div>
                    );
                })}
            </div>
        </div >
    );
}

/**
 * Camera content - shows live webcam feed with life overlay
 */
function CameraContent({
    player,
    streamMode,
    mediaStream,
    onToggleVideo,
    onToggleAudio,
    onRequestCamera,
    onSwitchCamera,
    videoEnabled = true,
    audioEnabled = true,
    isLocalPlayer = false,
    onLifeChange,
    compact = false,
    isLobby = false,
}: {
    player: Player;
    streamMode: boolean;
    mediaStream?: MediaStream | null;
    onToggleVideo?: () => void;
    onToggleAudio?: () => void;
    onRequestCamera?: () => void;
    onSwitchCamera?: () => void;
    videoEnabled?: boolean;
    audioEnabled?: boolean;
    isLocalPlayer?: boolean;
    onLifeChange?: (delta: number) => void;
    compact?: boolean;
    isLobby?: boolean;
}) {
    const videoRef = useRef<HTMLVideoElement>(null);

    // Attach stream to video element
    useEffect(() => {
        if (videoRef.current && mediaStream) {
            videoRef.current.srcObject = mediaStream;
        }
        return () => {
            if (videoRef.current) {
                videoRef.current.srcObject = null;
            }
        };
    }, [mediaStream]);

    // No stream — show enable prompt (local) or waiting state (remote)
    if (!mediaStream) {
        return (
            <div className="w-full h-full flex flex-col items-center justify-center bg-black/80 gap-3">
                {isLocalPlayer ? (
                    <>
                        <Camera className="size-8 text-white/15" />
                        <button
                            onClick={onRequestCamera}
                            className="px-4 py-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 text-xs font-medium border border-cyan-500/20 transition-all"
                        >
                            Enable Camera
                        </button>
                    </>
                ) : (
                    <>
                        <VideoOff className="size-6 text-white/10" />
                        <span className="text-[10px] text-white/20">No feed</span>
                    </>
                )}
                {/* Life overlay even without camera */}
                <div className="absolute bottom-2 right-3 pointer-events-none">
                    <span className={`text-3xl font-bold font-mono drop-shadow-lg ${player.life <= 0 ? 'text-red-500' : player.life <= 10 ? 'text-orange-400' : 'text-white/80'}`}>
                        {player.life}
                    </span>
                </div>
                {!isLobby && (
                    <div className="absolute bottom-2 left-3 pointer-events-none">
                        <span className="text-[10px] text-white/30 font-medium">{player.name}</span>
                    </div>
                )}
            </div>
        );
    }

    // Active stream — render video with overlays
    return (
        <div className="w-full h-full relative bg-black group/cam">
            {/* Video element */}
            <video
                ref={videoRef}
                autoPlay
                playsInline
                muted={isLocalPlayer} // Mute own audio to avoid feedback
                className={`w-full h-full object-cover ${!videoEnabled ? 'hidden' : ''} ${isLocalPlayer ? 'scale-x-[-1]' : ''}`}
            />

            {/* Video disabled overlay */}
            {!videoEnabled && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                    <VideoOff className="size-8 text-white/15" />
                </div>
            )}

            {/* Life total overlay — bottom-right, always visible */}
            <div className="absolute bottom-1.5 right-2 z-10">
                <div className="flex items-center gap-1">
                    {isLocalPlayer && onLifeChange && (
                        <button
                            onClick={() => onLifeChange(-1)}
                            className="w-6 h-6 rounded-full bg-black/40 hover:bg-red-500/30 text-white/40 hover:text-red-400 text-xs font-bold flex items-center justify-center opacity-0 group-hover/cam:opacity-100 transition-all"
                        >
                            −
                        </button>
                    )}
                    <span className={`text-2xl font-bold font-mono drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)] px-1 ${player.life <= 0 ? 'text-red-500' : player.life <= 10 ? 'text-orange-400' : 'text-white'}`}>
                        {player.life}
                    </span>
                    {isLocalPlayer && onLifeChange && (
                        <button
                            onClick={() => onLifeChange(1)}
                            className="w-6 h-6 rounded-full bg-black/40 hover:bg-green-500/30 text-white/40 hover:text-green-400 text-xs font-bold flex items-center justify-center opacity-0 group-hover/cam:opacity-100 transition-all"
                        >
                            +
                        </button>
                    )}
                </div>
            </div>

            {/* Name overlay — bottom-left */}
            {!isLobby && (
                <div className="absolute bottom-1.5 left-2 z-10 pointer-events-none">
                    <span className="text-[10px] text-white/50 font-medium drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                        {player.name}
                    </span>
                </div>
            )}

            {/* Camera controls — top-right, hover-reveal (local player only) */}
            {isLocalPlayer && (
                <div className="absolute top-1.5 right-1.5 z-20 flex items-center gap-1 opacity-0 group-hover/cam:opacity-100 transition-opacity">
                    {onToggleVideo && (
                        <button
                            onClick={onToggleVideo}
                            className={`p-1.5 rounded-full backdrop-blur-sm transition-all ${videoEnabled ? 'bg-black/40 text-white/60 hover:text-white' : 'bg-red-500/30 text-red-400'}`}
                            title={videoEnabled ? 'Turn off camera' : 'Turn on camera'}
                        >
                            {videoEnabled ? <Video className="size-3.5" /> : <VideoOff className="size-3.5" />}
                        </button>
                    )}
                    {onToggleAudio && (
                        <button
                            onClick={onToggleAudio}
                            className={`p-1.5 rounded-full backdrop-blur-sm transition-all ${audioEnabled ? 'bg-black/40 text-white/60 hover:text-white' : 'bg-red-500/30 text-red-400'}`}
                            title={audioEnabled ? 'Mute microphone' : 'Unmute microphone'}
                        >
                            {audioEnabled ? <Mic className="size-3.5" /> : <MicOff className="size-3.5" />}
                        </button>
                    )}
                    {onSwitchCamera && (
                        <button
                            onClick={onSwitchCamera}
                            className="p-1.5 rounded-full bg-black/40 backdrop-blur-sm text-white/60 hover:text-white transition-all"
                            title="Switch camera"
                        >
                            <SwitchCamera className="size-3.5" />
                        </button>
                    )}
                </div>
            )}

            {/* Audio indicator — shown when mic is live (remote players) */}
            {!isLocalPlayer && audioEnabled && (
                <div className="absolute top-1.5 right-1.5 z-20">
                    <Mic className="size-3 text-cyan-400/50" />
                </div>
            )}
        </div>
    );
}

/**
 * Playtester content - shows the actual DeckPlaytester or a load prompt
 */
function PlaytesterContent({
    player,
    streamMode,
    onLoadDeck,
    onClearDeck,
    onDeckImported
}: {
    player: Player;
    streamMode: boolean;
    onLoadDeck?: () => void;
    onClearDeck?: () => void;
    onDeckImported?: (deck: PlaytesterCard[], deckName?: string) => void;
}) {
    const [decklistText, setDecklistText] = useState('');
    const [isImporting, setIsImporting] = useState(false);
    const [importStatus, setImportStatus] = useState('');

    // If player has a deck loaded, render the actual DeckPlaytester
    if (player.deck && player.deck.length > 0) {
        return (
            <div className="w-full h-full relative">
                <DeckPlaytester
                    deck={player.deck}
                    deckName={player.deckName || 'Deck'}
                    onClose={() => onClearDeck?.()}
                    mode={streamMode ? 'session' : 'goldfish'}
                />
            </div>
        );
    }

    // Parse a single decklist line via Scryfall
    const parseLine = async (line: string): Promise<PlaytesterCard | null> => {
        const match = line.match(/^(\d+)x?\s+([^(]+?)(?:\s*\([^)]+\)\s*\d*)?$/i);
        if (!match) return null;
        const quantity = parseInt(match[1]);
        let cardName = match[2].trim();
        if (cardName.includes(' // ')) cardName = cardName.split(' // ')[0];
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000);
            const res = await fetch(
                `https://api.scryfall.com/cards/named?fuzzy=${encodeURIComponent(cardName)}`,
                { signal: controller.signal }
            );
            clearTimeout(timeoutId);
            if (res.ok) {
                const data = await res.json();
                const frontType = data.card_faces?.[0]?.type_line ?? data.type_line ?? '';
                const backImg = data.card_faces?.[1]?.image_uris?.normal ||
                    (data.card_faces?.[1] ? `https://cards.scryfall.io/normal/back/${data.id.slice(0, 1)}/${data.id.slice(1, 2)}/${data.id}.jpg` : undefined);
                return {
                    name: data.name,
                    quantity,
                    imageUrl: data.image_uris?.normal || data.card_faces?.[0]?.image_uris?.normal || '',
                    backImageUrl: backImg,
                    cmc: data.cmc || 0,
                    isLand: frontType.toLowerCase().includes('land'),
                    colors: data.colors || data.card_faces?.[0]?.colors || [],
                    type: data.type_line,
                };
            }
        } catch (e) {
            console.warn(`Failed to fetch ${cardName}:`, e);
        }
        return null;
    };

    const handleImport = async () => {
        const text = decklistText.trim();
        if (!text || !onDeckImported) return;
        setIsImporting(true);
        setImportStatus('Parsing...');
        try {
            // Check if it's a URL (Moxfield, Archidekt, etc.)
            const cards: PlaytesterCard[] = [];
            const lines = text.split('\n').map(l => l.trim()).filter(l => l && /^\d/.test(l));
            let imported = 0;
            for (const line of lines) {
                const card = await parseLine(line);
                if (card) cards.push(card);
                imported++;
                setImportStatus(`${imported}/${lines.length} cards...`);
                await new Promise(r => setTimeout(r, 80));
            }
            if (cards.length > 0) {
                // Try to derive deck name from commander or first card
                const deckName = cards.find(c => c.type?.toLowerCase().includes('legendary'))?.name || 'Imported Deck';
                onDeckImported(cards, deckName);
            } else {
                setImportStatus('No valid cards found');
            }
        } catch (err) {
            console.error('Import error:', err);
            setImportStatus('Import failed');
        } finally {
            setIsImporting(false);
        }
    };

    // No deck loaded - show inline import
    return (
        <div className="w-full h-full flex flex-col bg-gradient-to-br from-amber-900/20 to-purple-900/20 rounded-lg p-3 overflow-hidden">
            <div className="flex items-center gap-2 mb-2 flex-shrink-0">
                <Activity className="size-4 text-amber-400/60" />
                <span className="text-xs text-amber-300/80 font-medium">Playtester</span>
                {streamMode && (
                    <span className="text-[9px] text-cyan-400/60 ml-auto">Hand hidden</span>
                )}
            </div>

            <div className="flex-1 min-h-0 flex flex-col gap-2">
                <textarea
                    value={decklistText}
                    onChange={(e) => setDecklistText(e.target.value)}
                    placeholder={"Paste decklist here...\n4 Lightning Bolt\n4 Monastery Swiftspear\n20 Mountain"}
                    className="flex-1 w-full bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-white text-[10px] placeholder:text-white/30 focus:outline-none focus:border-purple-500/50 font-mono resize-none min-h-0"
                    disabled={isImporting}
                />

                <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                        onClick={handleImport}
                        disabled={isImporting || !decklistText.trim() || !onDeckImported}
                        className="px-3 py-1.5 bg-purple-500/30 hover:bg-purple-500/40 disabled:bg-white/5 disabled:text-white/30 text-purple-300 rounded-lg text-[10px] font-medium flex items-center gap-1.5 transition-colors border border-purple-500/30 disabled:border-white/10"
                    >
                        {isImporting ? (
                            <><Loader2 className="size-3 animate-spin" /> {importStatus}</>
                        ) : (
                            <><Upload className="size-3" /> Import Deck</>
                        )}
                    </button>
                    <span className="text-[9px] text-white/30">
                        {decklistText.trim() ? `${decklistText.trim().split('\n').filter(l => /^\d/.test(l.trim())).length} lines` : 'Format: 4 Card Name'}
                    </span>
                </div>
            </div>
        </div>
    );
}

export type { Player, PlayerMeans, PlayerFeatures, PlaytesterCard, PlayerContainerProps };
export default PlayerContainer;
